import Box from "@mui/material/Box";
import { useState, useEffect, useMemo } from "react";
import RGL, { WidthProvider, Layout } from "react-grid-layout";

import { AndonBoardLayoutItem } from "~/features/andon/andon.types";

import "./dragdrop.css";

interface NoCollisionLayoutProps {
  className: string;
  cols: number;
  width: number;
  rowHeight: number;
  onLayoutChange: (layout: Layout[]) => void;
  preventCollision?: boolean;
  layout: AndonBoardLayoutItem[];
  onExistingElementClick: (
    event: React.MouseEvent<HTMLElement>,
    id: string
  ) => void;
  onGridClick: (event: MouseEvent) => void;
  renderElementWithIndex: (index: string) => React.ReactElement | null;
  sizeLocked: boolean;
  isDraggable: boolean;
}

const ReactGridLayout = WidthProvider(RGL);

const NoCollisionLayout: React.FC<NoCollisionLayoutProps> = (
  props: NoCollisionLayoutProps
) => {
  const {
    className,
    cols,
    width,
    rowHeight,
    onLayoutChange,
    preventCollision,
    layout,
    onExistingElementClick,
    onGridClick,
    renderElementWithIndex,
    sizeLocked,
    isDraggable
  } = props;

  useEffect(() => {
    const handleGridClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const grid = document.querySelector(`.${className}`);

      if (grid && target === grid) {
        onGridClick(event);
      }
    };
    // Attach the event listener to the document
    document.addEventListener("click", handleGridClick);

    // Cleanup the event listener when the component is unmounted
    return () => {
      document.removeEventListener("click", handleGridClick);
    };
  }, [className, onGridClick]);

  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = () => {
    if (!isDragging) setIsDragging(true);
  };

  const onDragStop = () => {
    setTimeout(() => setIsDragging(false), 0);
  };

  const children = useMemo(() => {
    return layout.map((boardLayoutItem) => {
      const renderedElement = renderElementWithIndex(boardLayoutItem.i);

      return (
        <Box
          key={boardLayoutItem.i}
          role="presentation"
          data-grid={boardLayoutItem}
          onClick={(e) => {
            if (isDragging) return;
            onExistingElementClick(e, boardLayoutItem.i);
          }}
          sx={{ height: "100%", width: "100%" }}
        >
          {renderedElement}
        </Box>
      );
    });
  }, [layout, onExistingElementClick, renderElementWithIndex, isDragging]);

  return (
    <ReactGridLayout
      width={width}
      layout={layout}
      onLayoutChange={onLayoutChange}
      isDraggable={isDraggable}
      className={className}
      cols={cols}
      rowHeight={rowHeight - 10}
      compactType={null}
      preventCollision={preventCollision}
      onDrag={handleDrag}
      onDragStop={onDragStop}
      onResizeStart={handleDrag}
      onResizeStop={onDragStop}
      isBounded
      autoSize={false}
      isResizable={!sizeLocked}
    >
      {children}
    </ReactGridLayout>
  );
};

export default NoCollisionLayout;
